import { useMemo } from 'react';
import type { AgentInfo } from '../../utils/agents';
import type { ActivityEvent } from '../../types/activity';
import DeskAgent from './DeskAgent';
import type { AgentStatus } from './DeskAgent';
import type { Conversation } from './useConversations';
import {
  ZONES,
  ZONE_LABELS,
  ZONE_COLORS,
  ZONE_BORDER_COLORS,
  DEPT_TO_ZONE,
  computeAgentPositions,
} from './useConversations';

interface OfficeMapProps {
  agents: AgentInfo[];
  conversations: Conversation[];
  getAgentPosition: (slug: string) => { x: number; y: number };
  activityEvents: ActivityEvent[];
  pulsing: boolean;
}

function getAgentStatus(slug: string, events: ActivityEvent[]): AgentStatus {
  const oneHourAgo = Date.now() - 60 * 60 * 1000;
  const recentEvents = events.filter(
    (e) => e.agent === slug && new Date(e.timestamp).getTime() > oneHourAgo
  );

  if (recentEvents.length === 0) return 'idle';
  const latest = recentEvents[0];
  if (latest.status === 'error') return 'error';
  return 'working';
}

// Small desk furniture rectangles
function DeskRect({ x, y }: { x: number; y: number }) {
  return (
    <div
      className="absolute w-[2.5%] h-[1.5%] bg-bg-secondary/60 border border-border-subtle/30 rounded-sm"
      style={{ left: `${x}%`, top: `${y + 2.5}%` }}
    />
  );
}

export default function OfficeMap({
  agents,
  conversations,
  getAgentPosition,
  activityEvents,
  pulsing,
}: OfficeMapProps) {
  const positions = useMemo(() => computeAgentPositions(agents), [agents]);

  // Determine which agents are currently walking/talking and their override positions
  const conversationState = useMemo(() => {
    const walking = new Set<string>();
    const chatMessages = new Map<string, string>();
    const overridePositions = new Map<string, { x: number; y: number }>();

    for (const conv of conversations) {
      if (conv.phase === 'walking') {
        walking.add(conv.agent1Slug);
        // Agent1 walks toward agent2
        const target = positions.get(conv.agent2Slug);
        if (target) {
          overridePositions.set(conv.agent1Slug, {
            x: target.x - 3,
            y: target.y,
          });
        }
      } else if (conv.phase === 'talking') {
        // Both agents stay near agent2's position
        const target = positions.get(conv.agent2Slug);
        if (target) {
          overridePositions.set(conv.agent1Slug, {
            x: target.x - 3,
            y: target.y,
          });
        }
        // Show current chat message
        if (conv.currentMessageIndex >= 0 && conv.currentMessageIndex < conv.messages.length) {
          const msg = conv.messages[conv.currentMessageIndex];
          const speakerSlug =
            msg.speaker === 'agent1' ? conv.agent1Slug : conv.agent2Slug;
          chatMessages.set(speakerSlug, msg.text);
        }
      } else if (conv.phase === 'returning') {
        // Agent1 returns to desk (no override = back to original position)
        walking.add(conv.agent1Slug);
      }
    }

    return { walking, chatMessages, overridePositions };
  }, [conversations, positions]);

  return (
    <div className="relative w-full bg-bg-primary border border-border-subtle rounded-xl overflow-hidden"
         style={{ paddingBottom: '60%' /* 5:3 aspect ratio */ }}>
      <div className="absolute inset-0">
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '5% 5%',
          }}
        />

        {/* Zone backgrounds */}
        {Object.entries(ZONES).map(([zoneName, rect]) => (
          <div
            key={zoneName}
            className={`absolute ${ZONE_COLORS[zoneName]} border ${ZONE_BORDER_COLORS[zoneName]} rounded-lg`}
            style={{
              left: `${rect.x}%`,
              top: `${rect.y}%`,
              width: `${rect.w}%`,
              height: `${rect.h}%`,
            }}
          >
            {/* Zone label */}
            <div className="absolute top-1 left-2">
              <span className="text-[9px] font-medium text-text-muted/70 uppercase tracking-wider">
                {ZONE_LABELS[zoneName]}
              </span>
            </div>
          </div>
        ))}

        {/* Desk furniture (decorative) */}
        {agents.map((agent) => {
          const pos = positions.get(agent.slug);
          if (!pos) return null;
          return <DeskRect key={`desk-${agent.slug}`} x={pos.x - 1.25} y={pos.y} />;
        })}

        {/* Agent avatars */}
        {agents.map((agent) => {
          const homePos = positions.get(agent.slug);
          if (!homePos) return null;

          const overridePos = conversationState.overridePositions.get(agent.slug);
          const effectivePos = overridePos || homePos;
          const isWalking = conversationState.walking.has(agent.slug);
          const chatMessage = conversationState.chatMessages.get(agent.slug) || null;
          const status = getAgentStatus(agent.slug, activityEvents);

          return (
            <DeskAgent
              key={agent.slug}
              agent={agent}
              status={status}
              x={effectivePos.x}
              y={effectivePos.y}
              isWalking={isWalking}
              chatMessage={chatMessage}
              pulsing={pulsing}
            />
          );
        })}
      </div>
    </div>
  );
}
